/**
 * One pharmacy order: its events, exceptions and the operations actions (resubmit, cancel, manual
 * status, confirming a portal task). FUL-020 requires a note on every hand-entered status.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminPharmacyOrderDetail, PharmacyOrderState } from "@lattice/core";
import { formatUsd, ROUTES } from "@lattice/core";
import { useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { api } from "./api.ts";
import { Context, CopyValue, RecordLink } from "./bits.tsx";
import { Facts, History, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { href } from "./router.ts";
import { Button, ErrorText, Field, Link, SelectField, Status } from "./ui.tsx";
import { day, moment, ORDER_STATUS, PHARMACY_STATE, refusal } from "./words.ts";
type Action = "status" | "resubmit" | "cancel" | "complete";
function sendAction(action: Action, pharmacyOrderId: string, reason: string, state: string, externalOrderId: string) {
  const params = {
    pharmacyOrderId
  };
  const reasonBody = {
    reason
  };
  switch (action) {
    case "status":
      return api<AdminPharmacyOrderDetail>(ROUTES.adminPharmacyStatus, {
        params,
        body: {
          reason,
          state,
          externalOrderId
        }
      });
    case "resubmit":
      return api<AdminPharmacyOrderDetail>(ROUTES.adminPharmacyResubmit, {
        params,
        body: reasonBody
      });
    case "cancel":
      return api<AdminPharmacyOrderDetail>(ROUTES.adminPharmacyCancel, {
        params,
        body: reasonBody
      });
    case "complete":
      return api<AdminPharmacyOrderDetail>(ROUTES.adminPharmacyComplete, {
        params,
        body: {
          reason,
          externalOrderId
        }
      });
  }
}
function stateLabel(state: PharmacyOrderState): string {
  return PHARMACY_STATE[state];
}
function EventOutcome({
  outcome
}: {
  readonly outcome: "applied" | "refused";
}) {
  return outcome === "applied" ? <Status tone="ok">Applied</Status> : <Status tone="danger">Refused</Status>;
}
function ActionForm({
  action,
  detail,
  onDone,
  onCancel
}: {
  readonly action: Action;
  readonly detail: AdminPharmacyOrderDetail;
  readonly onDone: (next: AdminPharmacyOrderDetail) => void;
  readonly onCancel: () => void;
}) {
  const {
    order
  } = detail;
  const [state, setState] = useState(detail.nextStates[0] ?? order.state);
  const [portalId, setPortalId] = useState(order.externalId ?? "");
  const entering = action === "complete" && order.openTask?.kind === "enter_order";
  async function submit(reason: string): Promise<string | null> {
    const result = await sendAction(action, order.id, reason, state, portalId);
    if (!result.ok) return refusal(result.error);
    onDone(result.data);
    return null;
  }
  const copy = {
    status: {
      title: "Record a status",
      body: "Use this when the pharmacy told you something that has not arrived as an event. The note is stored on the event with your name.",
      action: "Record status",
      tone: "primary" as const
    },
    resubmit: {
      title: "Send to the pharmacy again",
      body: "A rejected order goes back to queued, then the pharmacy is asked again. A queued order is sent again as it stands.",
      action: "Resubmit",
      tone: "primary" as const
    },
    cancel: {
      title: "Cancel this pharmacy order",
      body: "If it was never sent, it is cancelled here. If the pharmacy already has it, you will still need to cancel it in their portal and confirm.",
      action: "Cancel order",
      tone: "danger" as const
    },
    complete: {
      title: entering ? "Confirm it is in the portal" : "Confirm the cancellation in the portal",
      body: entering ? "Enter the order in the pharmacy portal, then save the portal's reference here. That records submitted with your name." : "Cancel it in the pharmacy portal, then confirm. That records cancelled with your name.",
      action: entering ? "Mark entered" : "Mark cancelled in portal",
      tone: entering ? "primary" as const : "danger" as const
    }
  }[action];
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">{copy.title}</h2>
        <p className="x1ghz6dp x1jty25b">{copy.body}</p>
        <ReasonForm id={`${action}-reason`} hint="Logged with your name. Include the ticket number." action={copy.action} tone={copy.tone} onSubmit={submit} onCancel={onCancel} ready={action !== "status" || detail.nextStates.length > 0}>
          {action === "status" ? <SelectField id="manual-state" label="New status" value={state} options={detail.nextStates.map(next => ({
          value: next,
          label: stateLabel(next)
        }))} onChange={value => {
          const next = detail.nextStates.find(allowed => allowed === value);
          if (next !== undefined) setState(next);
        }} /> : null}
          {action === "status" || entering ? <Field id="portal-ref" label="Pharmacy reference" hint={entering ? "Required. The id the portal shows after you enter the order." : "Optional. Stored if the pharmacy gave one."} value={portalId} onChange={setPortalId} /> : null}
        </ReasonForm>
      </div>
    </Panel>;
}
function canResubmit(state: PharmacyOrderState): boolean {
  return state === "queued" || state === "rejected";
}
function Actions({
  detail,
  chosen,
  onChoose
}: {
  readonly detail: AdminPharmacyOrderDetail;
  readonly chosen: Action | null;
  readonly onChoose: (action: Action) => void;
}) {
  const {
    order
  } = detail;
  return <div className="x78zum5 x1a02dak x1173whf">
      {order.openTask === null ? null : <Button tone="primary" onClick={() => onChoose("complete")}>
          {order.openTask.kind === "enter_order" ? "Confirm portal entry" : "Confirm portal cancel"}
        </Button>}
      {detail.nextStates.length === 0 ? null : <Button tone="quiet" onClick={() => onChoose("status")}>
          Record status
        </Button>}
      {canResubmit(order.state) ? <Button tone="quiet" onClick={() => onChoose("resubmit")}>
          Resubmit
        </Button> : null}
      {order.state === "cancelled" || chosen === "cancel" ? null : <Button tone="quietDanger" onClick={() => onChoose("cancel")}>
          Cancel
        </Button>}
    </div>;
}
function Detail({
  detail,
  onChange
}: {
  readonly detail: AdminPharmacyOrderDetail;
  readonly onChange: (next: AdminPharmacyOrderDetail) => void;
}) {
  const [action, setAction] = useState<Action | null>(null);
  const {
    order
  } = detail;
  const who = order.email ?? order.customerName;
  return <>
      <PageHead back={<Link to={href.orders()}>Back to pharmacy orders</Link>} title={order.orderId} meta={<>
            <Status tone={order.openException === null ? "neutral" : "danger"}>
              {stateLabel(order.state)}
            </Status>
            {who === null || order.userId === null ? who === null ? null : <span>{who}</span> : <RecordLink to={href.user(order.userId)} relation="Account" label={who} />}
          </>} />
      <Split side={<>
            <Context title="This order" who={who ?? order.orderId} role={stateLabel(order.state)} can="You can resubmit, cancel and record status. You cannot change the formula." links={[...(order.userId === null ? [] : [{
        relation: "Account",
        label: who ?? "Account",
        to: href.user(order.userId)
      }]), {
        relation: "Patient",
        label: "Patient record",
        to: href.patient(order.patientId)
      }, {
        relation: "Orders",
        label: "All pharmacy orders",
        to: href.orders()
      }]} />
            <Section title="Details">
              <Facts items={[["Pharmacy order", <CopyValue key="id" value={order.id} label="pharmacy order id" />], ["Order", <CopyValue key="order" value={order.orderId} label="order id" />], ["Charge", formatUsd(order.amountCents)], ["Payment", ORDER_STATUS[order.orderStatus]], ["Pharmacy", order.integration === "manual" ? "Entered by hand" : order.integration], ["Portal reference", order.externalId ?? "None yet"], ["Held from", order.heldFrom === null ? "Not on hold" : stateLabel(order.heldFrom)], ["Created", day(order.createdAt)], ["Patient record", <RecordLink to={href.patient(order.patientId)} relation="Patient" label="Patient record" />]]} />
            </Section>
            {action === null ? <Panel padded>
                <div className="x78zum5 xdt5ytf x626il7">
                  <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">Actions</h2>
                  <p className="x1ghz6dp x1jty25b">
                    A status change needs a note. Nobody is emailed.
                  </p>
                  <Actions detail={detail} chosen={action} onChoose={setAction} />
                </div>
              </Panel> : <ActionForm key={action} action={action} detail={detail} onCancel={() => setAction(null)} onDone={next => {
        setAction(null);
        onChange(next);
      }} />}
          </>} main={<>
            <Section title="Exceptions" count={detail.exceptions.length}>
              <Table label="Exceptions" rows={detail.exceptions} rowKey={row => row.id} empty="No exceptions on this order." columns={[{
          label: "Kind",
          nowrap: true,
          cell: row => row.kind.replaceAll("_", " ")
        }, {
          label: "Status",
          nowrap: true,
          cell: row => row.status === "open" ? <Status tone="danger">Open</Status> : <Status tone="ok">Resolved</Status>
        }, {
          label: "Note",
          cell: row => row.note ?? "None"
        }, {
          label: "Opened",
          numeric: true,
          phoneLabel: true,
          cell: row => day(row.createdAt)
        }]} />
            </Section>
            <Section title="Events" count={detail.events.length}>
              <Table label="Fulfillment events" rows={detail.events} rowKey={row => row.id} empty="No events recorded yet." columns={[{
          label: "Status",
          nowrap: true,
          cell: row => stateLabel(row.state)
        }, {
          label: "Source",
          nowrap: true,
          phoneLabel: true,
          cell: row => row.source
        }, {
          label: "Outcome",
          nowrap: true,
          cell: row => <EventOutcome outcome={row.outcome} />
        }, {
          label: "Note",
          cell: row => row.note ?? "None"
        }, {
          label: "When",
          numeric: true,
          phoneLabel: true,
          cell: row => moment(row.occurredAt)
        }]} />
            </Section>
            <Section title="Staff actions" count={detail.history.length}>
              <History rows={detail.history} empty="No staff action on this order yet." />
            </Section>
          </>} />
    </>;
}
export function PharmacyOrder({
  id
}: {
  readonly id: string;
}) {
  const [loaded, replace] = useLoad(`pharmacy-order-${id}`, () => api<AdminPharmacyOrderDetail>(ROUTES.adminPharmacyOrder, {
    params: {
      pharmacyOrderId: id
    }
  }));
  if (loaded.state === "loading") return <PageHead title="Loading order" />;
  if (loaded.state === "failed") return <>
        <PageHead back={<Link to={href.orders()}>Back to pharmacy orders</Link>} title="Order" />
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </>;
  return <Detail detail={loaded.data} onChange={replace} />;
}